import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { api } from '../api.js';
import { useTheme, THEMES } from '../hooks/useTheme.js';
import { toast } from '../components/Toasts.jsx';
import { imapDefaultsFor } from '@hunter/shared/imapProviders';
import { inputCls } from '../components/ui';
import { formatClock, formatDate } from '../util/time.js';

// Settings = application-level preferences only.
// Anything job-hunt-related (target roles, locations, seniority, digest hour,
// excluded companies) lives on the Profile page next to the career profile.
// This page is for "how the app works for me": API keys, auth, future
// theme/timezone/notifications.

// T429 — what a stored secret's field should say.
//
// The placeholder was the masked value itself, so a saved password showed
// `••••••••` — dots in the one slot that is supposed to tell you what to
// type, and silent about the thing you actually want to know: that leaving
// it blank keeps what is there. Worse, `maskOpaque` returns
// `••••(invalid)` when the stored ciphertext will not decrypt — a real
// diagnostic (the mailbox cannot be opened and every scan fails quietly)
// rendered as a grey hint inside an empty-looking box.
//
// So: a sentence when it is saved, the plain hint when it is not, and the
// broken case lifted out of the placeholder entirely by `secretBroken`.
const SECRET_INVALID = '••••(invalid)';
function secretPlaceholder(stored, hint) {
  if (!stored) return hint;
  if (stored === SECRET_INVALID) return hint;
  return 'saved — leave blank to keep it';
}
const secretBroken = (stored) => stored === SECRET_INVALID;

export default function SettingsPage() {
  const { user, refresh } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [conn, setConn] = useState({
    anthropicKey: '',
    // T029 — IMAP connector. The backend has accepted these since T029 and the
    // worker has had a processor since T147, but no UI ever rendered them, so
    // the whole LinkedIn-email ingestion path was unreachable without calling
    // the API by hand. That is why imapscans has stayed at 0.
    imapHost: '',
    imapPort: '',
    imapUser: '',
    imapPassword: '',
    imapFolder: '',
    imapUnreadOnly: false,
    // T447 — the sending half.
    //
    // He told me he had "configured SMTP — host, user, app password", and he
    // had: into the five fields above. They ask for exactly those three
    // things and the same Gmail app password, so there was no way to tell
    // from the screen that only reading was wired. Now both halves are here,
    // under headings that say which is which.
    smtpHost: '',
    smtpPort: '',
    smtpUser: '',
    smtpPassword: '',
    smtpFromName: '',
    // T355 — a guess may fill a field, never overwrite one he typed.
    imapHostTouched: false,
    imapPortTouched: false,
  });
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  // T356 — the last probe result, or null when nothing has been tested yet.
  const [probe, setProbe] = useState(null);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);

  // T054 / T087 — AI spend for the current calendar month. Uses the new
  // /ai/usage endpoint which sums the full AiUsage ledger (all AI
  // features, not just rec explanations).
  const [usage, setUsage] = useState(null);
  const refetchUsage = () => {
    api.get('/ai/usage').then(setUsage).catch(() => setUsage(null));
  };
  useEffect(() => {
    refetchUsage();
  }, []);

  // T087 — AI controls (budget cap + auto-explain toggle). Local state
  // mirrors the user's prefs; PATCH to /preferences on Save.
  const [aiBudgetCapUsd, setAiBudgetCapUsd] = useState(
    user?.preferences?.aiBudgetCapUsd ?? '',
  );
  const [autoExplainEnabled, setAutoExplainEnabled] = useState(
    user?.preferences?.autoExplain?.enabled ?? false,
  );
  const [autoExplainTopN, setAutoExplainTopN] = useState(
    user?.preferences?.autoExplain?.topN ?? 10,
  );
  const [autoExplainMinScore, setAutoExplainMinScore] = useState(
    user?.preferences?.autoExplain?.minScore ?? 30,
  );
  const [aiSaving, setAiSaving] = useState(false);
  useEffect(() => {
    setAiBudgetCapUsd(user?.preferences?.aiBudgetCapUsd ?? '');
    setAutoExplainEnabled(user?.preferences?.autoExplain?.enabled ?? false);
    setAutoExplainTopN(user?.preferences?.autoExplain?.topN ?? 10);
    setAutoExplainMinScore(user?.preferences?.autoExplain?.minScore ?? 30);
  }, [user?.preferences]);
  const saveAiControls = async () => {
    setAiSaving(true);
    try {
      await api.patch('/preferences', {
        aiBudgetCapUsd: aiBudgetCapUsd === '' || aiBudgetCapUsd === null
          ? null
          : Number(aiBudgetCapUsd),
        autoExplain: {
          enabled: autoExplainEnabled,
          // T268(5) — `|| 10` and `|| 30` are falsy-coalescing, so a deliberate
          // 0 became the default: the hint says minScore 0 means "explain
          // everything", and typing it silently asked for 30 instead.
          topN: Number.isFinite(Number(autoExplainTopN)) && autoExplainTopN !== '' ? Number(autoExplainTopN) : 10,
          minScore: Number.isFinite(Number(autoExplainMinScore)) && autoExplainMinScore !== '' ? Number(autoExplainMinScore) : 30,
        },
      });
      await refresh?.();
      // T268(2) — the "Monthly cap" bar right below this form is drawn from
      // /ai/usage, fetched once on mount. Saving a new cap left the bar
      // measuring spend against the OLD one until a reload.
      refetchUsage();
      toast('AI controls saved', { kind: 'success' });
    } catch (e) {
      toast(e?.body?.message || e?.message || 'Save failed', { kind: 'error' });
    } finally {
      setAiSaving(false);
    }
  };

  // T055 — Browser notifications opt-in. Stored in localStorage so we
  // remember the choice across sessions; the page also checks the live
  // Notification.permission state in case the user revoked it externally.
  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
  );
  // The operator's own on/off choice, which is separate from the browser
  // permission and lived only in localStorage. Now it is state React can see.
  const [notifsOn, setNotifsOn] = useState(() => {
    try {
      return localStorage.getItem('hunter-notifications') === 'on';
    } catch {
      return false;
    }
  });
  const notifsEnabled =
    notifsOn && notifPerm === 'granted';
  const enableNotifs = async () => {
    if (typeof Notification === 'undefined') {
      toast('Browser does not support notifications', { kind: 'error' });
      return;
    }
    if (Notification.permission === 'denied') {
      toast('Notification permission was denied. Re-enable in the browser settings.', { kind: 'error' });
      return;
    }
    const perm = await Notification.requestPermission();
    setNotifPerm(perm);
    if (perm === 'granted') {
      localStorage.setItem('hunter-notifications', 'on');
      setNotifsOn(true);
      new Notification('hunter', { body: 'Notifications enabled. Scans will ping you when done.' });
      toast('Notifications enabled', { kind: 'success' });
    } else {
      toast('Notification permission not granted', { kind: 'error' });
    }
  };
  const disableNotifs = () => {
    localStorage.setItem('hunter-notifications', 'off');
    toast('Notifications muted (browser permission unchanged)', { kind: 'success' });
    // T268(1) — this was `setNotifPerm((p) => p)` with a "force re-render"
    // comment. React 18 BAILS OUT of an update whose reducer returns the same
    // value, so it forced nothing: the state the button is meant to flip lives
    // in localStorage, which React does not watch, and the UI kept showing
    // "enabled" until the next full page load.
    setNotifsOn(false);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      // Only send fields the operator actually typed — empty preserves
      // the existing encrypted value server-side.
      const payload = {};
      if (conn.anthropicKey.trim()) payload.anthropicKey = conn.anthropicKey.trim();

      // T268(4) — the probe has existed since T073 with a comment explaining
      // exactly why ("so a wrong key fails fast instead of dying later inside a
      // cover letter"), and its only caller was the onboarding wizard. A key
      // pasted from Settings — the normal way to rotate one — was stored
      // unvalidated, and the operator found out the next time he paid for an
      // AI action. Costs nothing: countTokens is a 0-token auth probe.
      if (payload.anthropicKey) {
        try {
          await api.post('/connectors/validate-anthropic', { anthropicKey: payload.anthropicKey });
        } catch (e) {
          const msg = e?.body?.message || e?.message || 'That Anthropic key was rejected.';
          setError(msg);
          toast(msg, { kind: 'error' });
          return;
        }
      }

      const imap = {};
      if (conn.imapHost.trim()) imap.host = conn.imapHost.trim();
      if (String(conn.imapPort).trim()) imap.port = Number(conn.imapPort);
      if (conn.imapUser.trim()) imap.user = conn.imapUser.trim();
      if (conn.imapPassword.trim()) imap.password = conn.imapPassword.trim();
      if (conn.imapFolder.trim()) imap.folder = conn.imapFolder.trim();
      // T403 — sent only when it DIFFERS from what the server holds.
      //
      // Sending it unconditionally was the first version and two existing
      // tests caught it within the minute: "sends the fields the form is
      // showing, and no others" started seeing an extra key, and "typing
      // nothing at all is refused" stopped being refused, because a payload
      // that always carries one field is never empty. Both invariants are
      // deliberate and both are worth more than the convenience.
      if (Boolean(conn.imapUnreadOnly) !== Boolean(currentConn.imap?.unreadOnly)) {
        imap.unreadOnly = Boolean(conn.imapUnreadOnly);
      }
      if (Object.keys(imap).length > 0) payload.imap = imap;

      const smtp = {};
      if (conn.smtpHost.trim()) smtp.host = conn.smtpHost.trim();
      if (String(conn.smtpPort).trim()) smtp.port = Number(conn.smtpPort);
      if (conn.smtpUser.trim()) smtp.user = conn.smtpUser.trim();
      if (conn.smtpPassword.trim()) smtp.password = conn.smtpPassword.trim();
      if (conn.smtpFromName.trim()) smtp.fromName = conn.smtpFromName.trim();
      if (Object.keys(smtp).length > 0) payload.smtp = smtp;

      if (Object.keys(payload).length === 0) {
        setError('No connector fields to update — type a value to overwrite.');
        toast('No connector fields to update — type a value to overwrite.', { kind: 'error' });
        return;
      }

      await api.patch('/connectors', payload);
      await refresh();
      // T403 — the text fields clear because empty means "unchanged"; a
      // checkbox has no such spelling, so it keeps the value just saved
      // rather than snapping back to unchecked.
      setConn((c) => ({
        anthropicKey: '', imapHost: '', imapPort: '', imapUser: '', imapPassword: '', imapFolder: '',
        smtpHost: '', smtpPort: '', smtpUser: '', smtpPassword: '', smtpFromName: '',
        imapUnreadOnly: Boolean(c.imapUnreadOnly), imapHostTouched: false, imapPortTouched: false,
      }));
      setSavedAt(new Date());
      // T356 — the caller needs to know. "Test connection" saves first when the
      // form is dirty, and testing after a failed save would report on the OLD
      // credentials while the operator is looking at the new ones.
      return true;
    } catch (e) {
      const msg = e.body?.message || e.message;
      setError(msg);
      toast(msg, { kind: 'error' });
      return false;
    } finally {
      setBusy(false);
    }
  };

  // T356 — "do these credentials work?" without running the pipeline. The
  // alternative was a full scan, which needs an Anthropic key and spends tokens
  // per email; that is how the operator discovered he had saved his Google
  // account password instead of an app password.
  // T356 review — a verdict describes the credentials it was run against, so
  // it dies the moment those change. Without this, the worst sequence is silent:
  // test with a good password → green "Connected" → type a WRONG password →
  // the green is still sitting there next to it. Every IMAP field clears it.
  const setImap = (patch) => {
    setProbe(null);
    setConn((c) => ({ ...c, ...(typeof patch === 'function' ? patch(c) : patch) }));
  };

  const testConnection = async () => {
    setTesting(true);
    setProbe(null);
    setError(null);
    try {
      // The endpoint tests what is STORED, so unsaved edits have to land first
      // or the verdict describes credentials he is no longer looking at.
      if (imapDirty) {
        const saved = await save();
        if (!saved) return;
      }
      setProbe(await api.post('/connectors/imap/test', {}));
    } catch (e) {
      const msg = e.body?.message || e.message || 'The connection test itself failed.';
      setProbe({ ok: false, verdict: 'request_failed', message: msg, folders: [] });
    } finally {
      setTesting(false);
    }
  };

  const currentConn = user?.connectors ?? {};

  // T403 — a checkbox has no "leave it alone" state the way an empty text
  // field does, so it has to start from what the server holds. Without this
  // it renders unchecked on every load and the next save turns the setting
  // off without him touching it.
  const serverUnreadOnly = Boolean(currentConn.imap?.unreadOnly);
  useEffect(() => {
    setConn((c) => (c.imapUnreadOnly === serverUnreadOnly ? c : { ...c, imapUnreadOnly: serverUnreadOnly }));
  }, [serverUnreadOnly]);
  // T356 — any typed IMAP field means the stored credentials are not what he is
  // looking at, so Test has to save before it tests.
  const imapDirty = Boolean(
    conn.imapHost.trim() ||
      String(conn.imapPort).trim() ||
      conn.imapUser.trim() ||
      conn.imapPassword.trim() ||
      conn.imapFolder.trim(),
  );

  return (
    <div className="space-y-8">
      <header className="border-b border-slate-800 pb-6">
        <h1 className="text-3xl font-semibold tracking-tight text-brand-500">Settings</h1>
        <p className="mt-1 text-sm text-slate-400">
          Application-level settings: API keys, account info.{' '}
          <span className="text-slate-500">
            Job-hunt preferences (target roles · locations · digest hour) live on the{' '}
            <a className="underline decoration-dotted hover:text-brand-500" href="/profile">
              Profile
            </a>{' '}
            page.
          </span>
        </p>
      </header>

      {/* T268(6) — the connector Save button is roughly five screens below
          this, so a failure was reported entirely off-screen and silently:
          no role, no toast, and the operator saw nothing happen. It keeps its
          place for the record, but now announces itself, and every setError
          above is paired with a toast that appears where he is looking. */}
      {error && (
        <p role="alert" className="rounded border border-red-800 bg-red-950/40 p-3 text-sm text-red-300">{error}</p>
      )}

      {/* ─── Account ─────────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-brand-500">Account</h2>
        <dl className="grid gap-2 text-sm sm:grid-cols-[auto_1fr] sm:gap-x-6">
          <dt className="text-slate-500">Email</dt>
          <dd className="text-slate-200">{user?.email}</dd>
          <dt className="text-slate-500">Role</dt>
          <dd className="text-slate-200">{user?.role}</dd>
          <dt className="text-slate-500">Member since</dt>
          <dd className="text-slate-200">
            {user?.createdAt ? formatDate(user.createdAt) : '—'}
          </dd>
        </dl>
        <p className="text-xs text-slate-500">
          Password change UI lands later. Until then, rotate via the{' '}
          <code className="font-mono">createUser.js</code> CLI inside the backend container.
        </p>

        {/* T080 — Re-run onboarding. Clears onboardedAt and navigates back
            to the wizard. Profile/preferences/connectors stay intact. */}
        <div className="rounded-lg border border-slate-800 bg-slate-900/30 p-3">
          <p className="text-xs font-medium text-slate-300">First-time setup wizard</p>
          <p className="mt-1 text-[11px] text-slate-500">
            Re-experience the onboarding wizard — useful for re-uploading a CV, resetting hunt preferences, or just orienting again. <strong>Your profile, applications, and stored credentials are not touched.</strong>
          </p>
          <button
            type="button"
            onClick={async () => {
              try {
                await api.post('/auth/reset-onboarding', {});
                await refresh?.();
                toast('Onboarding reset — starting over', { kind: 'success' });
                navigate('/onboarding');
              } catch (e) {
                toast(e?.body?.message || e?.message || 'Reset failed', { kind: 'error' });
              }
            }}
            className="mt-2 rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
          >
            Re-run onboarding wizard →
          </button>
        </div>
      </section>

      {/* ─── Appearance ──────────────────────────────────────────────────── */}
      <section className="space-y-3 border-t border-slate-800 pt-8">
        <div>
          <h2 className="text-sm font-medium text-brand-500">Appearance</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Pick a theme — applies instantly, preference saved locally.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {THEMES.map((t) => (
            <ThemeSwatch
              key={t.id}
              themeDef={t}
              active={theme === t.id}
              onPick={() => setTheme(t.id)}
            />
          ))}
        </div>
      </section>

      {/* ─── Notifications (T055) ────────────────────────────────────────── */}
      <section className="space-y-3 border-t border-slate-800 pt-8">
        <div>
          <h2 className="text-sm font-medium text-brand-500">Notifications</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Get a browser notification when a scan completes. Per-device.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-800 bg-slate-900/30 p-3 text-sm">
          <span className="flex-1">
            Status:{' '}
            <span className="font-mono text-slate-300">
              {notifPerm === 'unsupported'
                ? 'unsupported (no browser API)'
                : notifsEnabled
                  ? 'enabled'
                  : notifPerm === 'denied'
                    ? 'denied (re-enable in browser settings)'
                    : 'off'}
            </span>
          </span>
          {!notifsEnabled ? (
            <button
              type="button"
              onClick={enableNotifs}
              disabled={notifPerm === 'unsupported'}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-500 disabled:opacity-50"
            >
              Enable notifications
            </button>
          ) : (
            <button
              type="button"
              onClick={disableNotifs}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-red-600 hover:text-red-400"
            >
              Mute
            </button>
          )}
        </div>
      </section>

      {/* ─── AI Usage + Controls (T054 + T087) ──────────────────────────── */}
      <section className="space-y-4 border-t border-slate-800 pt-8">
        <div>
          <h2 className="text-sm font-medium text-brand-500">AI usage this month</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Anthropic spend across every AI feature. Resets on the 1st (UTC).
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <UsageCard label="Total calls" value={usage?.calls ?? '—'} />
          {/* T276 — the whole prompt, not the uncached remainder of it. The API
              moves cached tokens out of `tokensIn`, so the old figure went DOWN
              as caching improved; measured this month it was short by 25.8%. */}
          <UsageCard
            label="Input + output tokens"
            value={usage
              ? Intl.NumberFormat().format(
                  (usage.tokensIn ?? 0) + (usage.cachedIn ?? 0) + (usage.cacheWriteIn ?? 0) + (usage.tokensOut ?? 0),
                )
              : '—'}
            note={usage && (usage.cachedIn || usage.cacheWriteIn)
              ? `${Intl.NumberFormat().format((usage.cachedIn ?? 0) + (usage.cacheWriteIn ?? 0))} of it cached`
              : null}
          />
          <UsageCard
            label="Estimated cost"
            value={usage ? `$${(usage.costUsd ?? 0).toFixed(4)}` : '—'}
            highlight={!!(usage && usage.capUsd && (usage.costUsd ?? 0) / usage.capUsd >= 0.8)}
          />
        </div>

        {/* Cap progress bar when set */}
        {usage?.capUsd && (() => {
          const used = usage.costUsd ?? 0;
          const ratio = usage.capUsd ? used / usage.capUsd : 0;
          return (
            <div>
              <div className="flex items-baseline justify-between text-[11px] text-slate-400">
                <span>Monthly cap: ${usage.capUsd.toFixed(2)}</span>
                <span>{Math.round(ratio * 100)}% used</span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
                <div
                  className={
                    'h-full transition-all ' +
                    (ratio >= 1 ? 'bg-red-500' : ratio >= 0.8 ? 'bg-amber-500' : 'bg-brand-500')
                  }
                  style={{ width: `${Math.min(100, Math.round(ratio * 100))}%` }}
                />
              </div>
            </div>
          );
        })()}

        {/* Per-operation breakdown */}
        {usage?.breakdown?.length > 0 && (
          <details className="mt-2">
            <summary className="cursor-pointer text-[11px] text-slate-500 hover:text-slate-300">
              Spend by feature ({usage.breakdown.length})
            </summary>
            <ul className="mt-2 space-y-1 text-xs">
              {usage.breakdown.map((b) => (
                <li key={b.operation} className="flex items-baseline justify-between border-b border-slate-900 py-1">
                  <code className="font-mono text-slate-400">{b.operation}</code>
                  <span className="text-slate-400">
                    {b.calls} call{b.calls === 1 ? '' : 's'} · ${(b.costUsd ?? 0).toFixed(4)}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      {/* AI controls (cap + auto-explain) */}
      <section className="space-y-4 border-t border-slate-800 pt-8">
        <div>
          <h2 className="text-sm font-medium text-brand-500">AI controls</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Monthly budget cap + post-scan auto-explanation. The cap is a hard ceiling — batch features refuse once you cross it. Single-click features always work.
          </p>
        </div>

        <Section
          title="Monthly AI budget cap (USD)"
          hint="Leave empty for no cap. Recommended: $25 — covers all features on a typical scan day."
        >
          <input
            type="number"
            min="0"
            step="1"
            value={aiBudgetCapUsd}
            onChange={(e) => setAiBudgetCapUsd(e.target.value)}
            placeholder="e.g. 25"
            className="w-32 rounded-md border border-slate-700 bg-slate-950/40 px-3 py-1.5 text-sm text-slate-100"
          />
          <span className="ml-2 text-[11px] text-slate-500">
            {aiBudgetCapUsd === '' ? 'no cap' : `$${Number(aiBudgetCapUsd).toFixed(2)} / month`}
          </span>
        </Section>

        <Section
          title="Auto-explain post-scan"
          hint={
            autoExplainEnabled
              ? `After every scan, Claude explains the top ${autoExplainTopN} new recs (score ≥ ${autoExplainMinScore}). ~$${(autoExplainTopN * 0.005).toFixed(2)}-$${(autoExplainTopN * 0.02).toFixed(2)} per scan.`
              : 'Disabled. Run "✨ Explain match" on rows manually.'
          }
        >
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={autoExplainEnabled}
              onChange={(e) => setAutoExplainEnabled(e.target.checked)}
              className="h-4 w-4 cursor-pointer rounded border-slate-600 bg-slate-950/40 text-brand-500"
            />
            Enabled
          </label>
          {autoExplainEnabled && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400">top N</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={autoExplainTopN}
                  onChange={(e) => setAutoExplainTopN(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-1.5 text-sm text-slate-100"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400">min score</label>
                <input
                  type="number"
                  min="-100"
                  max="100"
                  value={autoExplainMinScore}
                  onChange={(e) => setAutoExplainMinScore(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-1.5 text-sm text-slate-100"
                />
              </div>
            </div>
          )}
        </Section>

        <button
          type="button"
          onClick={saveAiControls}
          disabled={aiSaving}
          className="rounded-md bg-brand-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-500 disabled:opacity-50"
        >
          {aiSaving ? 'saving…' : 'Save AI controls'}
        </button>
      </section>

      {/* T429 — the A/B block lived here and in Analytics; the Analytics copy
          stayed. Same endpoint, same numbers, two readings: there it is a
          collapsible section with a deterministic significance note, here it
          was a bare table that always rendered. Settings is where you change
          things, not where outcomes are read. Removing it also removes a fetch
          that ran on EVERY visit to this page for a section most visits never
          showed. Measured on all three accounts (A12): 7 picked pairs for the
          operator, 1 for Ana, 0 for Valentin, nothing generated since
          2026-08-13 — and the block's own copy calls anything under 6 too
          small to read. The capability is untouched; only the second copy is
          gone. */}

      {/* ─── Connectors ──────────────────────────────────────────────────── */}
      <section className="space-y-5 border-t border-slate-800 pt-8">
        <h2 className="text-sm font-medium text-brand-500">Connectors</h2>
        <p className="text-xs text-slate-500">
          API keys stored AES-256-GCM-encrypted at rest. Type a value to overwrite;
          empty input preserves the current value.
        </p>

        <Section
          title="Anthropic API key"
          hint={
            <>
              Current:{' '}
              <code className="font-mono text-slate-400">
                {currentConn.anthropicKey || '— not set —'}
              </code>
              {/* T421 — "upcoming AI features (T020 scoring, T025 cover-letter
                  generator, T031 interview prep)" — all three shipped long
                  ago, and ticket numbers are not something the operator can
                  look up. */}
              . Used by the CV parser, job scoring, cover letters, tailored CVs and interview
              prep. Get one at{' '}
              <a
                href="https://console.anthropic.com/settings/keys"
                target="_blank"
                rel="noreferrer"
                className="underline decoration-dotted hover:text-brand-500"
              >
                console.anthropic.com/settings/keys
              </a>
              .
            </>
          }
        >
          <input
            type="password"
            autoComplete="off"
            value={conn.anthropicKey}
            onChange={(e) => setConn((c) => ({ ...c, anthropicKey: e.target.value }))}
            className={inputCls}
            placeholder="sk-ant-api03-..."
          />
        </Section>

        <Section
          title="Email inbox (IMAP) — LinkedIn job alerts"
          hint={
            <>
              Read-only. hunter opens the folder you name below with <code className="font-mono">readOnly: true</code>,
              reads only mail from <code className="font-mono">linkedin.com</code>, and never
              deletes, moves or marks anything. It never touches LinkedIn itself — see the{' '}
              <a
                href="https://github.com/eraZnus/hunter.brandsradar.com/blob/main/docs/architecture/linkedin-bridge.md"
                target="_blank"
                rel="noreferrer"
                className="underline decoration-dotted hover:text-brand-500"
              >
                LinkedIn boundary
              </a>
              . Gmail needs an <strong>app password</strong> (Google blocks your normal password
              over IMAP): enable 2-step verification, then create one at{' '}
              <a
                href="https://myaccount.google.com/apppasswords"
                target="_blank"
                rel="noreferrer"
                className="underline decoration-dotted hover:text-brand-500"
              >
                myaccount.google.com/apppasswords
              </a>
              .{' '}
              {/* T355 — this used to end "Host imap.gmail.com, port 993", which
                  was an instruction to type two things the form now fills from
                  the address. What is left is the part no form can do for him. */}
              Type your address first and the host and port fill themselves.
            </>
          }
        >
          <div className="grid gap-2 sm:grid-cols-2">
            <input
              type="text"
              autoComplete="off"
              value={conn.imapHost}
              onChange={(e) => setImap({ imapHost: e.target.value, imapHostTouched: true })}
              className={inputCls}
              placeholder={currentConn.imap?.host || 'imap.gmail.com'}
              aria-label="IMAP host"
            />
            <input
              type="number"
              autoComplete="off"
              value={conn.imapPort}
              onChange={(e) => setImap({ imapPort: e.target.value, imapPortTouched: true })}
              className={inputCls}
              placeholder={String(currentConn.imap?.port || 993)}
              aria-label="IMAP port"
            />
            {/* T355 — typing the address fills the host and port.
                The operator asked whether Gmail could configure itself. OAuth
                cannot: `gmail.readonly` is a restricted scope, so a Testing-status
                app gets refresh tokens that expire every 7 days, and escaping
                that needs Google's full verification with a security audit — a
                weekly re-login forever, or an audit, for a one-person instance.
                Host and port, though, follow from the address, and a wrong host
                is the likeliest way a correct password looks broken. */}
            <input
              type="text"
              autoComplete="off"
              value={conn.imapUser}
              onChange={(e) => {
                const imapUser = e.target.value;
                const guess = imapDefaultsFor(imapUser);
                setProbe(null);
                setConn((c) => ({
                  ...c,
                  imapUser,
                  // Only fills what he has not typed over. A field he edited is
                  // his answer, and a guess must not overwrite an answer.
                  imapHost: guess && !c.imapHostTouched ? guess.host : c.imapHost,
                  imapPort: guess && !c.imapPortTouched ? String(guess.port) : c.imapPort,
                }));
              }}
              className={inputCls}
              placeholder={currentConn.imap?.user || 'you@gmail.com'}
              aria-label="IMAP username"
            />
            <input
              type="password"
              autoComplete="off"
              value={conn.imapPassword}
              onChange={(e) => setImap({ imapPassword: e.target.value })}
              className={inputCls}
              placeholder={secretPlaceholder(currentConn.imap?.password, 'app password')}
              aria-label="IMAP password"
            />
            {secretBroken(currentConn.imap?.password) && (
              <p className="mt-1 text-xs text-amber-400">
                The saved IMAP password cannot be read — most likely the encryption key changed.
                Fetch mail will fail until you type it again.
              </p>
            )}
            {/* T354 — the fifth field, and the one that decides whether the
                other four find anything. On Gmail the natural way to route job
                mail is a filter that labels it AND skips the inbox, which makes
                it invisible to a scanner that can only open INBOX. */}
            <input
              type="text"
              autoComplete="off"
              value={conn.imapFolder}
              onChange={(e) => setImap({ imapFolder: e.target.value })}
              className={inputCls}
              placeholder={currentConn.imap?.folder || 'INBOX'}
              aria-label="IMAP folder"
            />
          </div>
          {/* T403 — his idea: leave a message unread on purpose and hunter
              interprets it; read the ones you want left alone. Safe because
              the scanners open the mailbox readOnly, so hunter reading a
              message never clears the unread flag. */}
          <label className="mt-3 flex items-start gap-2">
            <input
              type="checkbox"
              checked={Boolean(conn.imapUnreadOnly)}
              onChange={(e) => setImap({ imapUnreadOnly: e.target.checked })}
              className="mt-0.5 shrink-0"
            />
            <span className="text-xs text-slate-300">
              Only read messages I have not opened
              <span className="mt-0.5 block text-slate-500">
                Leave a reply unread and hunter interprets it; open it yourself and hunter leaves it
                alone. Measured on your mailbox: this skips about 83% of the messages the scan would
                otherwise download and interpret. hunter never marks anything read.
              </span>
            </span>
          </label>
          <p className="mt-1 text-xs text-slate-500">
            Leave the folder empty for <code className="font-mono">INBOX</code>. If you file job mail
            under a Gmail label that skips the inbox, put the label here — otherwise the scan finds
            nothing and says so without an error. Changing it restarts the read from the beginning of
            that folder.
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Current:{' '}
            <code className="font-mono text-slate-400">
              {currentConn.imap?.host
                ? `${currentConn.imap.user || '?'} @ ${currentConn.imap.host}:${currentConn.imap.port || 993}`
                : '— not set —'}
            </code>
            . Once saved, run it from the dashboard's “📧 Fetch mail”.
          </p>
        </Section>

        {/* T447 — sending. Separate section, because the product had the
            reading half only and nothing on screen said so: 28 digests were
            generated and none delivered, and the operator reasonably
            believed he had configured mail because the form above asks for
            a host, a username and the same Gmail app password. */}
        <Section title="Email sending (SMTP)">
          <p className="mb-2 max-w-prose text-xs text-slate-500">
            Used to send follow-ups from your own address, one at a time and only when you press
            send. On Gmail this is <code className="font-mono">smtp.gmail.com</code> port{' '}
            <code className="font-mono">587</code> with the same app password as above — the
            reading settings do not cover sending.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <input
              type="text"
              autoComplete="off"
              value={conn.smtpHost}
              onChange={(e) => setConn((c) => ({ ...c, smtpHost: e.target.value }))}
              className={inputCls}
              placeholder={currentConn.smtp?.host || 'smtp.gmail.com'}
              aria-label="SMTP host"
            />
            <input
              type="number"
              autoComplete="off"
              value={conn.smtpPort}
              onChange={(e) => setConn((c) => ({ ...c, smtpPort: e.target.value }))}
              className={inputCls}
              placeholder={String(currentConn.smtp?.port || 587)}
              aria-label="SMTP port"
            />
            <input
              type="text"
              autoComplete="off"
              value={conn.smtpUser}
              onChange={(e) => setConn((c) => ({ ...c, smtpUser: e.target.value }))}
              className={inputCls}
              placeholder={currentConn.smtp?.user || 'you@gmail.com'}
              aria-label="SMTP username"
            />
            <input
              type="password"
              autoComplete="new-password"
              value={conn.smtpPassword}
              onChange={(e) => setConn((c) => ({ ...c, smtpPassword: e.target.value }))}
              className={inputCls}
              placeholder={secretPlaceholder(currentConn.smtp?.password, 'app password')}
              aria-label="SMTP password"
            />
            {secretBroken(currentConn.smtp?.password) && (
              <p className="mt-1 text-xs text-amber-400">
                The saved SMTP password cannot be read — most likely the encryption key changed.
                Sending will fail until you type it again.
              </p>
            )}
            <input
              type="text"
              autoComplete="off"
              value={conn.smtpFromName}
              onChange={(e) => setConn((c) => ({ ...c, smtpFromName: e.target.value }))}
              className={inputCls}
              placeholder={currentConn.smtp?.fromName || 'Your name (shown to the recipient)'}
              aria-label="SMTP from name"
            />
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Current:{' '}
            <code className="font-mono text-slate-400">
              {currentConn.smtp?.host
                ? `${currentConn.smtp.user || '?'} @ ${currentConn.smtp.host}:${currentConn.smtp.port || 587}`
                : '— not set — nothing can be sent —'}
            </code>
            {currentConn.smtp?.lastError && (
              <span className="ml-2 text-red-400">last send failed: {currentConn.smtp.lastError}</span>
            )}
          </p>
        </Section>

        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={save}
            // T356 review — `testConnection` calls save(), whose finally sets
            // busy false while the probe POST is still in flight. Without
            // `testing` here the Save button comes back to life mid-test and a
            // second click fires a patch alongside the probe.
            disabled={busy || testing}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-500 disabled:opacity-50"
          >
            {busy ? 'saving…' : 'Save connectors'}
          </button>
          {/* T356 — secondary next to Save: it is the step you take before you
              trust the primary one, not a competing action. */}
          <button
            type="button"
            onClick={testConnection}
            disabled={busy || testing}
            className="rounded-md border border-slate-700 px-4 py-2 text-sm font-medium text-slate-300 transition hover:border-slate-500 hover:text-slate-100 disabled:opacity-50"
          >
            {testing ? 'testing…' : imapDirty ? 'Save & test connection' : 'Test connection'}
          </button>
          {savedAt && !busy && !testing && (
            <span className="text-xs text-slate-500">saved {formatClock(savedAt)}</span>
          )}
        </div>
        <ProbeResult
          probe={probe}
          onPickFolder={(f) => {
            // Not setImap: clearing here would unmount the very panel the chip
            // lives in, so the list vanishes under the click that used it. The
            // verdict was about a different folder, though, so it is marked
            // stale rather than left standing.
            setConn((c) => ({ ...c, imapFolder: f }));
            setProbe((pv) => (pv ? { ...pv, stale: true } : pv));
          }}
        />
      </section>
    </div>
  );
}

// T356 — the verdict, and on a reachable server the folder list.
//
// The folder list is the half that turns a yes/no into something actionable:
// the operator's mailbox has labels like `Serviciu` and `[Gmail]/All Mail`, and
// until now the folder field was a free-text box you guessed into. Clicking a
// name fills the field.
function ProbeResult({ probe, onPickFolder }) {
  // T356 review — the live region is mounted ALWAYS, empty when there is
  // nothing to say. A region inserted into the DOM already containing its text
  // is not announced: screen readers watch existing regions for changes, so
  // conditional mounting meant the verdict was never read out. The wrapper is
  // permanent; only its contents change.
  if (!probe) return <div className="sr-only" role="status" aria-live="polite" />;
  const good = probe.ok && !probe.stale;
  const tone = good
    ? 'border-emerald-800 bg-emerald-950/30 text-emerald-300'
    : 'border-amber-800 bg-amber-950/20 text-amber-300';
  return (
    <div className={`rounded-md border px-3 py-2 text-xs ${tone}`} role="status" aria-live="polite">
      <div className="font-medium">
        {good ? '✓ ' : '✕ '}
        {probe.message}
        {probe.stale && ' — folder changed since this test; re-test to confirm.'}
      </div>
      {probe.verdict === 'folder_case_mismatch' && probe.suggestion && (
        <div className="mt-1 text-amber-200/80">
          Use <code className="font-mono">{probe.suggestion}</code>, not{' '}
          <code className="font-mono">{probe.folder}</code>.
        </div>
      )}
      {probe.verdict === 'folder_missing' && (
        <div className="mt-1 text-amber-200/80">
          Looking for <code className="font-mono">{probe.folder}</code>. Pick one below.
        </div>
      )}
      {probe.folders?.length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-wide text-slate-400">
            Folders on the server — click one to use it
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {probe.folders.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => onPickFolder(f)}
                className="rounded border border-slate-700 bg-slate-900/60 px-1.5 py-0.5 font-mono text-[11px] text-slate-300 transition hover:border-brand-600 hover:text-brand-400"
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UsageCard({ label, value, highlight, note }) {
  return (
    <div
      className={
        'rounded-lg border p-3 ' +
        (highlight ? 'border-brand-700 bg-brand-600/10' : 'border-slate-800 bg-slate-900/40')
      }
    >
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-lg font-semibold ${highlight ? 'text-brand-500' : 'text-slate-200'}`}>{value}</div>
      {note && <div className="mt-0.5 text-[10px] text-slate-500">{note}</div>}
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide text-slate-400">{title}</span>
      {hint && <span className="mt-0.5 block text-xs text-slate-500">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

// Mini UI preview tile rendered with inline-style hex from the theme swatch
// so the preview is faithful regardless of the *currently* active theme.
function ThemeSwatch({ themeDef, active, onPick }) {
  const [bg, surface, accent, up] = themeDef.swatch;
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={active}
      aria-label={`Apply ${themeDef.label} theme`}
      className="relative rounded-xl border-2 p-3 text-left transition-transform hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[var(--c-bg)]"
      style={{
        backgroundColor: bg,
        borderColor: active ? accent : 'rgba(255,255,255,0.08)',
        boxShadow: active ? `0 0 0 2px ${accent}` : undefined,
      }}
    >
      <div style={{ backgroundColor: surface }} className="mb-2 rounded-lg p-2">
        <div className="mb-1.5 flex items-center gap-1">
          <span style={{ backgroundColor: accent }} className="h-2 w-2 rounded-full" />
          <span
            style={{ backgroundColor: up, opacity: 0.7 }}
            className="h-1.5 flex-1 rounded"
          />
        </div>
        <span
          style={{ backgroundColor: accent, opacity: 0.25 }}
          className="mb-1 block h-1 w-full rounded"
        />
        <span
          style={{ backgroundColor: accent, opacity: 0.12 }}
          className="block h-1 w-3/4 rounded"
        />
      </div>
      <span style={{ color: accent }} className="text-xs font-semibold leading-tight">
        {themeDef.label}
      </span>
      {active && (
        <span
          style={{ backgroundColor: accent, color: bg }}
          className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold"
          aria-hidden="true"
        >
          ✓
        </span>
      )}
    </button>
  );
}
